import { trim } from 'lodash'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerPrivateRegionNodeHardware } from '@/api/type-server'
import { Loading, Trans } from '@/components'
import { Pagination } from '@/components/base/Pagination'
import { formatMillicentPrice } from '@/utils/formatter'
import { formatGB } from '@/utils/storage'
import { cn } from '@/utils/style'

import { getHardwareStatusColor, getHardwareStatusTextKey } from '../utils/nodeFormatters'

interface GpusInfoTableProps {
  hardwares: ServerPrivateRegionNodeHardware[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
}

export const GpusInfoTable = memo(function GpusInfoTable({
  hardwares,
  loading,
  error,
  onRetry,
  total,
  page,
  pageSize,
  onPageChange,
}: GpusInfoTableProps) {
  const { t } = useTranslation('privateRegion')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-red-500 t-s text-center py-8">
        {error}
        <button className="ml-2 text-highlight underline" onClick={onRetry}>
          {t('common.retry')}
        </button>
      </div>
    )
  }

  if (hardwares === null) {
    return null
  }

  if (hardwares.length === 0) {
    return (
      <div className="text-tertiary t-s text-center py-8">
        <Trans ns="privateRegion" i18nKey="common.noData">
          No data
        </Trans>
      </div>
    )
  }

  return (
    <>
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[16%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.gpuName">
                GPU Name
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[12%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.gpuStatus">
                GPU Status
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[10%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.vgpus">
                vGPUs
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[12%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.vrams">
                vRAMs
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[10%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.vcpus">
                vCPUs
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[12%]">
              <Trans ns="privateRegion" i18nKey="computeNode.storage">
                Storage
              </Trans>
            </th>
            <th className="text-tertiary t-x-sb px-4 py-3 text-left w-[18%]">
              <Trans ns="privateRegion" i18nKey="nodeDetail.pricePerHour">
                Price/Hr
              </Trans>
            </th>
          </tr>
        </thead>
        <tbody className="bg-panel-white">
          {hardwares.map((hw, index) => {
            const formatted = formatGB(hw.diskGiB, { dynamicDecimals: true })
            const [storageValue, storageUnit] = formatted.split(' ')
            const priceFormatted = String(formatMillicentPrice(hw.priceUSDMillicent, false, 'USD'))
            const priceParts = priceFormatted.split(' ')
            const priceUnit = priceParts.pop()
            const priceValue = priceParts.join(' ')

            return (
              <tr key={index}>
                <td className="t-x-sb text-secondary px-4 py-3">{trim(hw.gpuName, '""') || '-'}</td>
                <td className={cn('t-x-sb px-4 py-3', getHardwareStatusColor(hw.status))}>
                  {t(getHardwareStatusTextKey(hw.status))}
                </td>
                <td className="t-x-sb text-primary px-4 py-3">{hw.gpuNum}</td>
                <td className="t-x-sb text-primary px-4 py-3">
                  {(hw.ramMiB / 1024).toFixed(2)} <span className="text-tertiary">GB</span>
                </td>
                <td className="t-x-sb text-primary px-4 py-3">{hw.vCpuCores}</td>
                <td className="t-x-sb text-primary px-4 py-3">
                  {storageValue} <span className="text-tertiary">{storageUnit}</span>
                </td>
                <td className="t-x-sb text-primary px-4 py-3">
                  {priceValue} <span className="text-tertiary">{priceUnit}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {total > 0 && (
        <Pagination
          activeClassName="bg-panel-white"
          navClassName="bg-panel-white"
          size={pageSize}
          total={total}
          current={page}
          onChange={onPageChange}
          className="justify-end mt-6"
        />
      )}
    </>
  )
})
